import { isChatOpenAtom } from '@/pages/docs/store/doc-store'
import { useAtom } from 'jotai'
import {
  History,
  Plus,
  X,
  ArrowLeft,
  PanelRightOpen,
  PictureInPicture2,
} from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { currentThreadIdAtom, chatThreadsAtom } from './store/chatThreadStore'
import type { UIMessage } from 'ai'
import { ChatThreadTitle } from './components/ChatThreadTitle'

interface ChatHeaderProps {
  onNewThread: () => void
  onHistory: () => void
  isHistoryOpen: boolean
  onBack: () => void
  onClose?: () => void
  messages?: UIMessage[]
  chatStatus?: 'streaming' | 'awaiting_message' | 'in_progress' | 'error' | 'ready'
  onUpdateThreadName?: (params: { threadId: string; name: string }) => Promise<void>
  mode?: 'sidebar' | 'floating'
  onToggleMode?: () => void
  onToggleExpand?: () => void
  isExpanded?: boolean
  title?: string
  hideActions?: boolean
  hideCloseButton?: boolean
}

export default function ChatHeader(props: ChatHeaderProps) {
  const {
    onNewThread,
    onHistory,
    isHistoryOpen = false,
    onBack,
    onClose,
    messages = [],
    chatStatus,
    onUpdateThreadName,
    mode,
    onToggleMode,
    title,
    hideActions = false,
    hideCloseButton = false,
  } = props
  const [currentThreadId] = useAtom(currentThreadIdAtom)
  const [threads] = useAtom(chatThreadsAtom)
  const [, setIsChatOpen] = useAtom(isChatOpenAtom)

  const currentThread = threads.find(t => t.id === currentThreadId)

  const toggleChat = () => {
    setIsChatOpen(false)
    onClose?.()
  }

  return (
    <header className='flex flex-row justify-between gap-2.5 h-[50px] shrink-0 items-center border-b border-border pr-3 pl-2'>
      {isHistoryOpen ? (
        <div className='flex items-center gap-2 text-sm leading-6 font-medium'>
          <Button
            variant='ghost'
            className='h-7 w-7 rounded-sm p-0.5'
            onClick={onBack}
          >
            <ArrowLeft />
          </Button>
          History
        </div>
      ) : title ? (
        <div className='text-sm leading-6 font-medium'>
          {title}
        </div>
      ) : (
        <div className='flex min-w-0 flex-1 items-center gap-2'>
          <img
            src='/logos/screenata-logo-white-on-black-256.png'
            alt='Screenata'
            className='h-5 w-5 shrink-0 rounded-xs dark:hidden'
          />
          <img
            src='/logos/screenata-logo-black-on-white-256.png'
            alt='Screenata'
            className='hidden h-5 w-5 shrink-0 rounded-xs dark:block'
          />
          {currentThreadId && onUpdateThreadName ? (
            <ChatThreadTitle
              threadId={currentThreadId}
              threadName={currentThread?.name || null}
              messages={messages}
              chatStatus={chatStatus}
              onUpdateThreadName={onUpdateThreadName}
              onClick={onHistory}
            />
          ) : (
            <button
              onClick={onHistory}
              className='truncate text-sm leading-6 font-medium hover:opacity-80 transition-opacity'
            >
           {currentThread?.name || 'Screenata'}
            </button>
          )}
        </div>
      )}

      {!hideActions && (
        <div className='inline-flex shrink-0 items-center gap-1'>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 rounded-md p-0.5'
            onClick={onNewThread}
          >
            <Plus className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 rounded-md p-0.5'
            onClick={onHistory}
          >
            <History className='h-4 w-4' />
          </Button>

          {onToggleMode && (
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 rounded-md p-0.5'
              onClick={onToggleMode}
              title={mode === 'floating' ? 'Dock sidebar' : 'Switch to floating'}
            >
              {mode === 'floating' ? (
                <PanelRightOpen className='h-4 w-4' />
              ) : (
                <PictureInPicture2 className='h-4 w-4' />
              )}
            </Button>
          )}

          {/* <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 rounded-md p-0.5'
          >
            <MoreHorizontal className='h-4 w-4' />
          </Button> */}
          {/* 
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 rounded-md p-0.5'
            onClick={onExpand}
          >
            <PanelRight className='h-4 w-4' />
          </Button> */}

          {!hideCloseButton && (
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 rounded-md p-0.5'
              onClick={toggleChat}
            >
              <X className='h-4 w-4' />
            </Button>
          )}
        </div>
      )}
    </header>
  )
}
